"use client"

import React from "react"
import { cn } from "../../utils"
import { TButton } from "../button/TButton"
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger
} from "../shadcn/confirm-modal"

/**
 * @description 확인/취소 모달. 삭제 등 파괴적 액션 확인에 사용
 * @example
 * <TConfirmModal
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   title="삭제 확인"
 *   description="정말 삭제하시겠습니까?"
 *   actionLabel="삭제"
 *   onAccept={handleDelete}
 * />
 */
interface TConfirmModalProps {
	/** 모달 열림 상태 */
	open: boolean
	/** 열림 상태 변경 콜백 */
	onOpenChange: (open: boolean) => void
	/** 모달 제목 */
	title: string
	/** 모달 본문 커스텀 콘텐츠 */
	children?: React.ReactNode
	/** 모달 설명 텍스트 */
	description?: string | React.ReactNode
	/** 설명 영역의 추가 스타일(줄바꿈 등). */
	descriptionClassName?: string
	/** 모달 트리거 요소 */
	trigger?: React.ReactNode
	/**
	 * 취소 버튼 라벨
	 * @default "Cancel"
	 */
	cancelLabel?: string
	/**
	 * 확인 버튼 라벨
	 * @default "Confirm"
	 */
	actionLabel?: string
	/** 취소 버튼 숨김 */
	hiddenCancel?: boolean
	/** 확인 버튼 숨김 */
	hiddenAction?: boolean
	/** 푸터 좌측에 표시할 추가 액션 */
	tertiaryAction?: React.ReactNode
	/** 확인 버튼 비활성화 */
	disabled?: boolean
	/** 확인 버튼 클릭 콜백 */
	onAccept?: () => void
	/**
	 * 확인(action) 버튼이 파괴적 액션인 경우. true 면 색상 위치를 swap 한다 —
	 * 취소 버튼이 primary 색상(=안전한 기본 행동), 확인 버튼이 line 색상(=명시적 위험 행동) 으로 표시.
	 * 변경사항 폐기/계정 삭제 같은 비가역 액션의 nudging UX 에 사용.
	 * 시멘틱(onAccept = action) 은 그대로 보존되며 시각 강조만 swap.
	 */
	destructiveAction?: boolean
}

export function TConfirmModal(props: TConfirmModalProps) {
	const {
		open,
		onOpenChange,
		title,
		children,
		description,
		descriptionClassName,
		trigger,
		cancelLabel = "Cancel",
		actionLabel = "Confirm",
		hiddenCancel,
		hiddenAction,
		tertiaryAction,
		disabled,
		onAccept,
		destructiveAction
	} = props

	const handleClose = () => onOpenChange(false)

	const handleAccept = () => {
		onAccept?.()
		handleClose()
	}

	return (
		<AlertDialog open={open} onOpenChange={onOpenChange}>
			{trigger && <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>}
			<AlertDialogContent onEscapeKeyDown={(e) => e.preventDefault()}>
				<AlertDialogHeader className="text-left">
					<AlertDialogTitle>{title}</AlertDialogTitle>
					{typeof description === "string" ? (
						<AlertDialogDescription className={descriptionClassName}>{description}</AlertDialogDescription>
					) : (
						<>
							<AlertDialogDescription className="sr-only">{title}</AlertDialogDescription>
							{description && <div className={cn("text-muted-foreground text-base", descriptionClassName)}>{description}</div>}
						</>
					)}
					{!description && <AlertDialogDescription className="sr-only">{title}</AlertDialogDescription>}
				</AlertDialogHeader>
				{children}
				<AlertDialogFooter>
					{tertiaryAction && <div className="mr-auto">{tertiaryAction}</div>}
					{!hiddenCancel && (
						<AlertDialogCancel asChild>
							<TButton color={destructiveAction ? "primary" : "line"} onClick={handleClose}>
								{cancelLabel}
							</TButton>
						</AlertDialogCancel>
					)}
					{!hiddenAction && (
						<AlertDialogAction asChild>
							<TButton color={destructiveAction ? "line" : "primary"} disabled={disabled} onClick={handleAccept}>
								{actionLabel}
							</TButton>
						</AlertDialogAction>
					)}
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	)
}

TConfirmModal.displayName = "TConfirmModal"
